import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import './MobileSidebar.css';

function MobileSidebar({ isOpen, onClose }) {
  // Lock body scroll while open, close on Escape
  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  const sidebarContent = (
    <>
      {/* Overlay */}
      <div
        className={`sidebar-overlay ${isOpen ? 'open' : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Sidebar Panel */}
      <div
        id="mobile-sidebar"
        role="dialog"
        aria-modal="true"
        aria-hidden={!isOpen}
        className={`fixed top-0 left-0 h-full w-[85vw] max-w-[320px] sm:w-80 bg-white shadow-2xl z-[70] overflow-y-auto transition-transform duration-300 flex flex-col ${isOpen ? 'open' : ''}`}
      >
        {/* Sidebar Header */}
        <div className="p-5 sm:p-6 border-b border-gray-100 flex items-center justify-between shrink-0">
          <Link to="/" onClick={onClose} className="min-w-0">
            <img
              src="/logo.png"
              alt="Interconnect Systems Logo"
              className="h-9 sm:h-11 w-auto object-contain"
            />
          </Link>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-cogInk hover:bg-gray-50 rounded-full p-2 -mr-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-cogCyan transition-colors shrink-0"
            aria-label="Close menu"
          >
            <X className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        </div>

        {/* Sidebar Links */}
        <nav className="flex-1 px-5 sm:px-6 py-4 sm:py-6 text-base sm:text-lg font-medium">
          <Link to="/" onClick={onClose} className="block py-3 sm:py-3.5 border-b border-gray-100 text-cogInk hover:text-cogCyan active:text-cogCyan transition-colors">
            Home
          </Link>
          <Link to="/about" onClick={onClose} className="block py-3 sm:py-3.5 border-b border-gray-100 text-cogInk hover:text-cogCyan active:text-cogCyan transition-colors">
            About Us
          </Link>
          <Link to="/services" onClick={onClose} className="block py-3 sm:py-3.5 border-b border-gray-100 text-cogInk hover:text-cogCyan active:text-cogCyan transition-colors">
            Services
          </Link>
          <Link to="/products" onClick={onClose} className="block py-3 sm:py-3.5 border-b border-gray-100 text-cogInk hover:text-cogCyan active:text-cogCyan transition-colors">
            Products &amp; Design
          </Link>
          <Link to="/contact" onClick={onClose} className="block py-3 sm:py-3.5 border-b border-gray-100 text-cogInk hover:text-cogCyan active:text-cogCyan transition-colors">
            Contact
          </Link>
        </nav>

        {/* Call to Action */}
        <div className="p-5 sm:p-6 pt-0 shrink-0">
          <Link
            to="/contact"
            onClick={onClose}
            className="w-full block text-center bg-cogCyan hover:bg-cyan-400 active:bg-cyan-500 text-cogInk font-semibold py-3.5 sm:py-4 rounded-full text-sm sm:text-base transition-colors"
          >
            Request a Quote
          </Link>
        </div>
      </div>
    </>
  );

  // Portal to document.body: fixed positioning now resolves against the real
  // viewport, immune to backdrop-blur/transform on ancestors like <header>.
  return createPortal(sidebarContent, document.body);
}

export default MobileSidebar;